import { type TFunction } from "i18next";
import { format } from "date-fns";
import { zhCN } from "date-fns/locale/zh-CN";
import { enUS } from "date-fns/locale/en-US";
import { es } from "date-fns/locale/es";
import { ar } from "date-fns/locale/ar";
import { ptBR } from "date-fns/locale/pt-BR";
import { hi } from "date-fns/locale/hi";
import { ta } from "date-fns/locale/ta";
import { te } from "date-fns/locale/te";

const mlLocale = {
  ...te,
  localize: {
    ...te.localize,
    day: (dayIndex) => {
      const malayalamDays = [
        "ഞായർ", // Sunday (0)
        "തിങ്കൾ", // Monday (1)
        "ചൊവ്വ", // Tuesday (2)
        "ബുധൻ", // Wednesday (3)
        "വ്യാഴം", // Thursday (4)
        "വെള്ളി", // Friday (5)
        "ശനി" // Saturday (6)
      ];
      return malayalamDays[dayIndex];
    },
    month: (monthIndex) => {
      const malayalamMonths = [
        "ജനുവരി", // January (0)
        "ഫെബ്രുവരി", // February (1)
        "മാർച്ച്", // March (2)
        "ഏപ്രിൽ", // April (3)
        "മേയ്", // May (4)
        "ജൂൺ", // June (5)
        "ജൂലൈ", // July (6)
        "ഓഗസ്റ്റ്", // August (7)
        "സെപ്റ്റംബർ", // September (8)
        "ഒക്ടോബർ", // October (9)
        "നവംബർ", // November (10)
        "ഡിസംബർ" // December (11)
      ];
      return malayalamMonths[monthIndex];
    }
  }
};

// 语言代码到 date-fns locale 的映射
const localeMap = {
  en: enUS,
  "en-US": enUS,
  zh: zhCN,
  "zh-CN": zhCN,
  "zh-Hant": zhCN, // 暂时使用简体中文，可以后续添加繁体支持
  es: es,
  "es-ES": es,
  ar: ar,
  "ar-SA": ar,
  pt: ptBR,
  "pt-PT": ptBR,
  hi: hi,
  "hi-IN": hi,
  ta: ta,
  "ta-IN": ta,
  te: te,
  "te-IN": te,
  ml: mlLocale,
  "ml-IN": mlLocale
};

/**
 * 根据当前语言获取对应的 date-fns locale
 * @param language - 当前语言代码
 * @returns date-fns locale 对象
 */
export function getDateFnsLocale(language: string) {
  return localeMap[language as keyof typeof localeMap] || enUS;
}

/**
 * 支持多语言的时间格式化函数
 * @param date - 要格式化的日期
 * @param formatStr - 格式字符串
 * @param language - 语言代码
 * @returns 格式化后的日期字符串
 */
export function formatWithLocale(
  date: Date | number,
  formatStr: string,
  language: string
): string {
  const locale = getDateFnsLocale(language);
  return format(date, formatStr, { locale });
}

/**
 * 使用浏览器原生 Intl.DateTimeFormat 的备用格式化函数
 * @param date - 要格式化的日期
 * @param language - 语言代码
 * @param options - Intl.DateTimeFormat 选项
 * @returns 格式化后的日期字符串
 */
export function formatWithIntl(
  date: Date | number,
  language: string,
  options?: Intl.DateTimeFormatOptions
): string {
  const dateObj = typeof date === "number" ? new Date(date) : date;

  // 默认格式选项
  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    ...options
  };

  return new Intl.DateTimeFormat(language, defaultOptions).format(dateObj);
}

export function getRelativeTime(timestamp: number): string {
  const now = Date.now();
  const diff = now - timestamp;

  const seconds = Math.floor(diff / 1000);
  return formatRelativeTimeFromSeconds(seconds);
}

/**
 * Original function without i18n support (kept for backward compatibility)
 */
export function formatRelativeTimeFromSeconds(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const months = Math.floor(days / 30);
  const years = Math.floor(months / 12);

  if (years > 0) {
    return `${years} year${years > 1 ? "s" : ""} ago`;
  }
  if (months > 0) {
    return `${months} month${months > 1 ? "s" : ""} ago`;
  }
  if (days > 0) {
    return `${days} day${days > 1 ? "s" : ""} ago`;
  }
  if (hours > 0) {
    return `${hours} hour${hours > 1 ? "s" : ""} ago`;
  }
  if (minutes > 0) {
    return `${minutes} minute${minutes > 1 ? "s" : ""} ago`;
  }
  return "just now";
}

/**
 * i18n-enabled version of formatRelativeTimeFromSeconds
 * @param seconds - Number of seconds elapsed
 * @param t - Translation function from react-i18next
 * @returns Formatted relative time string with i18n support
 */
export function formatRelativeTimeFromSecondsI18n(
  seconds: number,
  t: TFunction,
  lastSaved: boolean = true
): string {
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const months = Math.floor(days / 30);
  const years = Math.floor(months / 12);

  if (years > 0) {
    return (
      (lastSaved ? t("time.lastSaved") : "") +
      t("time.relativeTime.yearsAgo", { count: years })
    );
  }
  if (months > 0) {
    return (
      (lastSaved ? t("time.lastSaved") : "") +
      t("time.relativeTime.monthsAgo", { count: months })
    );
  }
  if (days > 0) {
    return (
      (lastSaved ? t("time.lastSaved") : "") +
      t("time.relativeTime.daysAgo", { count: days })
    );
  }
  if (hours > 0) {
    return (
      (lastSaved ? t("time.lastSaved") : "") +
      t("time.relativeTime.hoursAgo", { count: hours })
    );
  }
  if (minutes > 0) {
    return (
      (lastSaved ? t("time.lastSaved") : "") +
      t("time.relativeTime.minutesAgo", { count: minutes })
    );
  }
  // return t("time.relativeTime.justNow");
  return "";
}

/**
 * i18n-enabled version of getRelativeTime
 * @param timestamp - Timestamp to compare against current time
 * @param t - Translation function from react-i18next
 * @returns Formatted relative time string with i18n support
 */
export function getRelativeTimeI18n(
  timestamp: number,
  t: TFunction,
  lastSaved: boolean = true
): string {
  const now = Date.now();
  const diff = now - timestamp;

  const seconds = Math.floor(diff / 1000);
  return formatRelativeTimeFromSecondsI18n(seconds, t, lastSaved);
}
